import BaseAPI from 'api/api'
import { FindAllInterface } from 'api/api-interface'

// Enums
export enum RANK_CONDITION_TYPE {
  // ยอดเงิน
  TOTAL_SPENDING = 'TOTAL_SPENDING',           // ยอดใช้จ่ายรวม
  AVERAGE_SPENDING = 'AVERAGE_SPENDING',       // ยอดใช้จ่ายเฉลี่ยต่อครั้ง
  SINGLE_RECEIPT = 'SINGLE_RECEIPT',           // ยอดใบเสร็จเดียว
  PRODUCT_PURCHASE = 'PRODUCT_PURCHASE',       // ยอดซื้อสินค้า
  TREATMENT_SPENDING = 'TREATMENT_SPENDING',   // ยอดค่าหัตถการ
  
  // การมาใช้บริการ
  VISIT_COUNT = 'VISIT_COUNT',                 // จำนวนครั้งที่มา
  CONSECUTIVE_VISITS = 'CONSECUTIVE_VISITS',   // การมาติดต่อกัน
  LAST_VISIT_DAYS = 'LAST_VISIT_DAYS',         // ไม่เกินกี่วันที่มาล่าสุด
  APPOINTMENT_KEPT = 'APPOINTMENT_KEPT',       // รักษานัดหมาย (%)
  
  // หัตถการ
  TREATMENT_TYPE = 'TREATMENT_TYPE',           // ประเภทหัตถการ
  TREATMENT_COURSE = 'TREATMENT_COURSE',       // คอร์สหัตถการ
  SPECIFIC_TREATMENT = 'SPECIFIC_TREATMENT',   // หัตถการเฉพาะ
  
  // อื่นๆ
  REFERRAL_COUNT = 'REFERRAL_COUNT',           // จำนวนการแนะนำ
  MEMBERSHIP_DURATION = 'MEMBERSHIP_DURATION', // ระยะเวลาสมาชิก
  POINTS_BALANCE = 'POINTS_BALANCE',           // ยอดคะแนนสะสม
  BIRTHDAY_MONTH = 'BIRTHDAY_MONTH'            // เดือนเกิด (สำหรับโปรโมชั่นพิเศษ)
}

export enum RANK_PERIOD_TYPE {
  LIFETIME = 'LIFETIME',
  YEARLY = 'YEARLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  CUSTOM = 'CUSTOM'
}

export enum CONDITION_OPERATOR {
  GTE = '>=',
  GT = '>',
  EQ = '=',
  LTE = '<=',
  LT = '<'
}

export enum CALCULATION_MODE {
  REALTIME = 'REALTIME',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY'
}

// Interfaces
export interface RankConditionInterface {
  rankConditionId?: number
  rankId: number
  conditionType: RANK_CONDITION_TYPE
  operator?: CONDITION_OPERATOR
  value: number
  periodType?: RANK_PERIOD_TYPE
  periodMonths?: number
  operativeTypeId?: number
  productTypeId?: number
  isRequired?: boolean
  priority?: number
  status?: 'ACTIVE' | 'INACTIVE'
}

export interface RankUpgradeRuleInterface {
  ruleId?: number
  fromRankId?: number
  toRankId: number
  logicType?: 'AND' | 'OR'
  autoDowngrade?: boolean
  downgradeAfterDays?: number
  gracePeriodDays?: number
  notifyBeforeDowngrade?: boolean
  notifyDaysBefore?: number
  status?: 'ACTIVE' | 'INACTIVE'
}

export interface CalculationSettingsInterface {
  settingId?: number
  branchId?: number
  calculationMode: CALCULATION_MODE
  calculationTime?: string
  includeOverdue?: boolean
  includeRefund?: boolean
  includeTreatmentOnly?: boolean
  includeProductOnly?: boolean
  resetPeriod?: 'NEVER' | 'YEARLY' | 'FISCAL_YEAR'
  fiscalYearStartMonth?: number
  autoUpgradeEnabled?: boolean
  autoDowngradeEnabled?: boolean
  notifyOnUpgrade?: boolean
  notifyOnDowngrade?: boolean
  status?: 'ACTIVE' | 'INACTIVE'
}

export interface PatientRankHistoryInterface {
  historyId: number
  patientId: number
  fromRankId?: number
  toRankId: number
  changeType: 'UPGRADE' | 'DOWNGRADE' | 'MANUAL' | 'INITIAL' | 'EXPIRED'
  reason?: string
  totalSpending?: number
  visitCount?: number
  effectiveDate: string
  expiryDate?: string
  createdAt: string
  createdBy?: string
}

export interface ManualRankChangeInterface {
  patientId: number
  newRankId: number
  reason?: string
  effectiveDate?: string
  expiryDate?: string
}

export default class MemberRankApi extends BaseAPI {
  // Rank Conditions
  static getConditions(rankId: number): Promise<any> {
    return this.api.get(`member-rank/conditions/${rankId}`).then((res) => res)
  }

  static createCondition(body: RankConditionInterface): Promise<any> {
    return this.api.post('member-rank/conditions', body).then((res) => res)
  }

  static updateCondition(id: number, body: Partial<RankConditionInterface>): Promise<any> {
    return this.api.patch(`member-rank/conditions/${id}`, body).then((res) => res)
  }

  static deleteCondition(id: number): Promise<any> {
    return this.api.delete(`member-rank/conditions/${id}`).then((res) => res)
  }

  // Upgrade Rules
  static getUpgradeRules(): Promise<any> {
    return this.api.get('member-rank/upgrade-rules').then((res) => res)
  }

  static createUpgradeRule(body: RankUpgradeRuleInterface): Promise<any> {
    return this.api.post('member-rank/upgrade-rules', body).then((res) => res)
  }

  static updateUpgradeRule(id: number, body: Partial<RankUpgradeRuleInterface>): Promise<any> {
    return this.api.patch(`member-rank/upgrade-rules/${id}`, body).then((res) => res)
  }

  static deleteUpgradeRule(id: number): Promise<any> {
    return this.api.delete(`member-rank/upgrade-rules/${id}`).then((res) => res)
  }

  // Patient Evaluation
  static evaluatePatient(patientId: number): Promise<any> {
    return this.api.get(`member-rank/evaluate/${patientId}`).then((res) => res)
  }

  // Manual Rank Change
  static manualRankChange(body: ManualRankChangeInterface): Promise<any> {
    return this.api.post('member-rank/manual-change', body).then((res) => res)
  }

  // Patient Rank History
  static getPatientHistory(patientId: number, props?: FindAllInterface): Promise<any> {
    return this.api.get(`member-rank/history/${patientId}`, { params: { ...props } }).then((res) => res)
  }

  // Calculation Settings
  static getSettings(branchId?: number): Promise<any> {
    const params = branchId ? { branchId } : {}
    return this.api.get('member-rank/settings', { params }).then((res) => res)
  }

  static updateSettings(body: Partial<CalculationSettingsInterface>): Promise<any> {
    return this.api.patch('member-rank/settings', body).then((res) => res)
  }

  // Batch Processing
  static processAllPatients(): Promise<any> {
    return this.api.post('member-rank/process-all').then((res) => res)
  }
}
